// PostHog owns native UTM, referrer, session and channel attribution.
// These legacy initial_* aliases are only a cookie-to-server conversion bridge.
export const INITIAL_MARKETING_ATTRIBUTION_KEYS = [
  "initial_utm_source",
  "initial_utm_medium",
  "initial_utm_campaign",
  "initial_utm_content",
  "initial_utm_term",
  "initial_utm_id",
] as const
export const FIRST_TOUCH_ATTRIBUTION_KEYS = [
  "initial_referrer",
  "initial_landing_path",
] as const

export type InitialMarketingAttributionKey =
  (typeof INITIAL_MARKETING_ATTRIBUTION_KEYS)[number]
export type FirstTouchAttributionKey =
  (typeof FIRST_TOUCH_ATTRIBUTION_KEYS)[number]

export function mergeFirstTouchMarketingAttribution(
  freshAttribution: Record<string, string>,
  persistedAttribution: Record<string, string> | null
): Record<string, string> {
  // Keep one complete snapshot. Missing first-touch values must not be filled
  // with a later visit's campaign, content or referrer.
  return { ...(persistedAttribution ?? freshAttribution) }
}

export function getInitialMarketingAttribution(
  attribution: Record<string, string>
): Partial<Record<InitialMarketingAttributionKey, string>> {
  return Object.fromEntries(
    INITIAL_MARKETING_ATTRIBUTION_KEYS.flatMap((key) => {
      const value = attribution[key] ?? attribution[key.replace("initial_", "")]
      return value ? [[key, value]] : []
    })
  )
}

// Existing ad-network metadata has no native PostHog equivalent. Never register
// or spread cookie UTM values into native browser event UTM fields.
export function getSupplementalMarketingAttribution(
  attribution: Record<string, string>
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(attribution).filter(
      ([key]) => key.startsWith("ts_") || key === "experiment_id" || key === "angle"
    )
  )
}

export function getInitialPersonMarketingAttribution(
  attribution: Record<string, string>
): Partial<Record<InitialMarketingAttributionKey | FirstTouchAttributionKey, string>> {
  const initial = getInitialMarketingAttribution(attribution)
  // Retain existing coarse aliases for compatibility; no campaign/content/path
  // on the person and no writes to PostHog's native $initial_* properties.
  return {
    ...(initial.initial_utm_source ? { initial_utm_source: initial.initial_utm_source } : {}),
    ...(initial.initial_utm_medium ? { initial_utm_medium: initial.initial_utm_medium } : {}),
    ...(attribution.initial_referrer ? { initial_referrer: attribution.initial_referrer } : {}),
  }
}
